import React from 'react';

// ──────────────────────────────────────────────
// INDI Privacy Policy
// Effective Date: March 2026
// ──────────────────────────────────────────────

export default function PrivacyPolicy({ onBack }) {
  const sectionStyle = {
    marginBottom: '2rem',
  };
  const headingStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#E8ECF1',
    marginBottom: '0.75rem',
  };
  const textStyle = {
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: '0.9375rem',
    lineHeight: '1.7',
    color: '#94A3B8',
    marginBottom: '0.75rem',
  };
  const listItemStyle = {
    ...textStyle,
    paddingLeft: '1.25rem',
    position: 'relative',
    marginBottom: '0.4rem',
  };
  const dotStyle = {
    position: 'absolute',
    left: 0,
    color: '#06B6D4',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#060A13',
      color: '#E8ECF1',
      fontFamily: "'Instrument Sans', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
      `}</style>

      <div style={{
        maxWidth: '740px',
        margin: '0 auto',
        padding: '4rem 2rem',
      }}>
        {/* Back link */}
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#06B6D4',
            fontFamily: "'Instrument Sans', sans-serif",
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            marginBottom: '2rem',
            padding: 0,
          }}
        >
          ← Back to INDI
        </button>

        {/* Header */}
        <h1 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '2.25rem',
          fontWeight: '800',
          letterSpacing: '-0.03em',
          marginBottom: '0.5rem',
        }}>
          Privacy Policy
        </h1>
        <p style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.8125rem',
          color: '#64748B',
          marginBottom: '3rem',
        }}>
          Effective Date: March 4, 2026 &nbsp;|&nbsp; Last Updated: March 4, 2026
        </p>

        {/* Introduction */}
        <div style={sectionStyle}>
          <p style={textStyle}>
            INDI Platform LLC ("INDI," "we," "us," or "our") operates the website located at 
            indi.space (the "Site"). This Privacy Policy describes how we collect, use, disclose, 
            and protect your personal information when you visit our Site, sign up for our waitlist, 
            submit applications, or otherwise interact with us.
          </p>
          <p style={textStyle}>
            By accessing or using the Site, you agree to the collection and use of information 
            in accordance with this Privacy Policy. If you do not agree, please do not use the Site.
          </p>
        </div>

        {/* 1. Information We Collect */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Information We Collect</h2>
          
          <p style={{ ...textStyle, color: '#B0B8C8', fontWeight: '600' }}>Information You Provide Directly</p>
          <p style={textStyle}>When you interact with our Site, you may voluntarily provide us with the following:</p>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Name and email address (when joining our community or waitlist)</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Project details including titles, synopses, budgets, and genre information (when submitting a filmmaker application)</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Investment preferences and accreditation status (when registering investor interest)</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>Portfolio or professional URLs you choose to share</div>

          <p style={{ ...textStyle, color: '#B0B8C8', fontWeight: '600', marginTop: '1rem' }}>Information Collected Automatically</p>
          <p style={textStyle}>
            When you visit the Site, we may automatically collect certain technical information 
            including your IP address, browser type, device information, referring URLs, and 
            pages visited. This information is collected through standard web technologies 
            such as cookies and server logs.
          </p>
        </div>

        {/* 2. How We Use Your Information */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. How We Use Your Information</h2>
          <p style={textStyle}>We use the information we collect for the following purposes:</p>
          <div style={listItemStyle}><span style={dotStyle}>·</span>To communicate with you about INDI, including updates, announcements, and responses to your inquiries</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>To process and evaluate filmmaker applications and investor interest registrations</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>To improve and develop our Site and future platform</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>To comply with legal obligations and enforce our terms</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>To protect the security and integrity of our Site</div>
        </div>

        {/* 3. How We Share Your Information */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. How We Share Your Information</h2>
          <p style={textStyle}>
            We do not sell, rent, or trade your personal information to third parties. 
            We may share your information in the following limited circumstances:
          </p>
          <div style={listItemStyle}><span style={dotStyle}>·</span>With service providers who assist us in operating the Site (such as hosting, email delivery, and analytics), subject to confidentiality obligations</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>When required by law, regulation, legal process, or governmental request</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>To protect the rights, property, or safety of INDI, our users, or the public</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>In connection with a merger, acquisition, or sale of assets, with notice to affected users</div>
        </div>

        {/* 4. Data Retention */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Data Retention</h2>
          <p style={textStyle}>
            We retain your personal information for as long as necessary to fulfill the purposes 
            described in this Privacy Policy, unless a longer retention period is required or 
            permitted by law. You may request deletion of your personal information by contacting 
            us at the email address provided below.
          </p>
        </div>

        {/* 5. Data Security */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Data Security</h2>
          <p style={textStyle}>
            We implement reasonable administrative, technical, and physical safeguards to protect 
            your personal information against unauthorized access, alteration, disclosure, or 
            destruction. However, no method of transmission over the Internet or electronic 
            storage is completely secure, and we cannot guarantee absolute security.
          </p>
        </div>

        {/* 6. Your Rights */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Your Rights</h2>
          <p style={textStyle}>Depending on your location, you may have the following rights regarding your personal information:</p>
          <div style={listItemStyle}><span style={dotStyle}>·</span>The right to access the personal information we hold about you</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>The right to request correction of inaccurate information</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>The right to request deletion of your personal information</div>
          <div style={listItemStyle}><span style={dotStyle}>·</span>The right to opt out of marketing communications at any time</div>
          <p style={{ ...textStyle, marginTop: '0.75rem' }}>
            To exercise any of these rights, please contact us at the email address below. 
            We will respond to your request within 30 days.
          </p>
        </div>

        {/* 7. California Residents */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. California Residents</h2>
          <p style={textStyle}>
            If you are a California resident, you have additional rights under the California 
            Consumer Privacy Act (CCPA), including the right to know what personal information 
            we collect, the right to request deletion, and the right to opt out of the sale of 
            personal information. As noted above, we do not sell personal information. To exercise 
            your CCPA rights, contact us using the information below.
          </p>
        </div>

        {/* 8. Children's Privacy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Children's Privacy</h2>
          <p style={textStyle}>
            The Site is not directed to individuals under the age of 18. We do not knowingly 
            collect personal information from children. If we become aware that we have 
            inadvertently collected information from a child under 18, we will take steps 
            to delete that information promptly.
          </p>
        </div>

        {/* 9. Third-Party Links */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Third-Party Links</h2>
          <p style={textStyle}>
            The Site may contain links to third-party websites or services. We are not 
            responsible for the privacy practices of those third parties. We encourage 
            you to review the privacy policies of any third-party sites you visit.
          </p>
        </div>

        {/* 10. Changes to This Policy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Changes to This Privacy Policy</h2>
          <p style={textStyle}>
            We may update this Privacy Policy from time to time. When we do, we will revise 
            the "Last Updated" date at the top of this page. We encourage you to review this 
            Privacy Policy periodically. Your continued use of the Site after any changes 
            constitutes your acceptance of the updated policy.
          </p>
        </div>

        {/* 11. Contact */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Contact Us</h2>
          <p style={textStyle}>
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <div style={{
            padding: '1.25rem',
            background: 'rgba(6, 182, 212, 0.04)',
            border: '1px solid rgba(6, 182, 212, 0.1)',
            borderRadius: '12px',
            marginTop: '0.75rem',
          }}>
            <p style={{ ...textStyle, marginBottom: '0.25rem', color: '#E8ECF1', fontWeight: '600' }}>INDI Platform LLC</p>
            <p style={{ ...textStyle, marginBottom: '0' }}>Email: info@indi.space</p>
          </div>
        </div>
      </div>
    </div>
  );
}
